import React, { useRef, useState } from 'react';
import { Grid } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { NodeFormData } from '../../../../../connector/connector-form/interfaces/nodeFormData';
import HdFormikDropDown from '../../../../../form/formik/components/dropdown/HdFormikDropDown';
import HdFormControl from '../../../../../form/components/form-control/HdFormControl';
import ActivateTargetAPI from '../../../ActivateTargetAPI';
import { ConnectorAuthTypeEnum } from '../../../../../connector/connector-form/interfaces/authType';
import CustomOptionWithSubtext from '../../../../../dropdown/components/dropdown-custom-options/CustomOptionWithSubtext';

interface GoogleSheetsProps {
  formMetaData?: any;
  formData: NodeFormData;
}

const EMPTY_SPREADSHEET = {
  spreadsheet: null,
  spreadsheetId: '',
  spreadsheetName: ''
};

export default function GoogleSheets({ formMetaData, formData }: GoogleSheetsProps) {
  const [spreadsheetLoading, setSpreadsheetLoading] = useState(false);
  const [foldersList, setFoldersList] = useState(formMetaData.folders);
  const [spreadsheetList, setSpreadsheetList] = useState(formMetaData.spreadsheets);
  const abortController = useRef(new AbortController());

  const accountId = formData.oauthTokenId || formData.serviceAccountId;
  const isAuthTypeServiceAccount = ConnectorAuthTypeEnum.SERVICE === formData.authType;
  const canCreateNewSpreadsheet =
    !isAuthTypeServiceAccount || (isAuthTypeServiceAccount && formData.folder);
  const formikProps = useFormikContext<{
    folder: object | null;
    folderId: string;
    spreadsheet: object | null;
    spreadsheetId: string;
    spreadsheetName: string;
  }>();

  const createNewFolder = async (folderName: string) => {
    const { data } = await ActivateTargetAPI.createNewFolder(
      accountId,
      formData.authType,
      folderName
    );
    if (data) {
      const latestOption = {
        id: data.folder_id,
        name: folderName,
        url: data.url
      };
      setFoldersList([...foldersList, latestOption]);
      formikProps.setValues({
        ...formikProps.values,
        folder: latestOption,
        folderId: latestOption.id,
        ...EMPTY_SPREADSHEET
      });
    }
  };

  const createNewSpreadsheet = async (spreadsheetName: string) => {
    const { data } = await ActivateTargetAPI.createNewSpreadsheet(
      accountId,
      formData.authType,
      spreadsheetName,
      formData.folder?.id
    );
    if (data) {
      const latestOption = {
        id: data.spreadsheet_id,
        name: spreadsheetName,
        url: data.url
      };
      setSpreadsheetList([...spreadsheetList, latestOption]);
      formikProps.setValues({
        ...formikProps.values,
        spreadsheet: latestOption,
        spreadsheetId: data.spreadsheet_id,
        spreadsheetName
      });
    }
  };

  const onChangeFolderEventHandler = selected => {
    if (selected) {
      formikProps.setValues({
        ...formikProps.values,
        folder: selected,
        folderId: selected.id,
        ...EMPTY_SPREADSHEET
      });
      if (spreadsheetLoading) {
        abortController.current.abort();
        abortController.current = new AbortController();
      }
      setSpreadsheetLoading(true);
      ActivateTargetAPI.getGoogleSheetSpreadsheets(accountId, formData.authType, selected.id, {
        signal: abortController.current.signal
      }).then(({ data }) => {
        setSpreadsheetList(data?.files || []);
        setSpreadsheetLoading(false);
      });
    } else {
      formikProps.setValues({
        ...formikProps.values,
        folder: null,
        folderId: '',
        ...EMPTY_SPREADSHEET
      });
      setSpreadsheetList([]);
    }
  };

  const onChangeSpreadsheetEventHandler = selected => {
    if (selected) {
      formikProps.setValues({
        ...formikProps.values,
        spreadsheet: selected,
        spreadsheetId: selected.id,
        spreadsheetName: selected.name
      });
    } else {
      formikProps.setValues({
        ...formikProps.values,
        spreadsheet: null,
        spreadsheetId: '',
        spreadsheetName: ''
      });
    }
  };

  const folderHelperText = () =>
    !isAuthTypeServiceAccount ? (
      <span>
        Displays all Drive folders created by or shared with you. Select an existing folder or
        create one now in your <i>My Drive</i> folder.
      </span>
    ) : (
      <span>You can only view the folders that have been shared with the service account.</span>
    );

  const spreadsheetHelperText = () =>
    !isAuthTypeServiceAccount ? (
      <span>
        Select an existing spreadsheet created by or shared with you or create one now. The
        spreadsheet is created in your <i>My Drive</i> folder if no folder is selected.
      </span>
    ) : (
      <span>
        You can view only the spreadsheets shared with the service account. To create a spreadsheet,
        the parent folder must be shared with the service account.
      </span>
    );

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={12}>
          <HdFormControl>
            <Field
              name='folder'
              label='Folder (Optional)'
              helperText={folderHelperText()}
              prefixIcon='folder'
              options={foldersList}
              CustomOption={CustomOptionWithSubtext}
              creatable={!isAuthTypeServiceAccount}
              creatableLabel='Folder'
              creatableCallback={createNewFolder}
              component={HdFormikDropDown}
              onChangeEventHandler={onChangeFolderEventHandler}
            />
          </HdFormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={12}>
          <HdFormControl>
            <Field
              name='spreadsheet'
              label='Spreadsheet'
              helperText={spreadsheetHelperText()}
              required
              prefixIcon='sheets'
              showLoading={spreadsheetLoading}
              options={spreadsheetList}
              CustomOption={CustomOptionWithSubtext}
              creatable={!!canCreateNewSpreadsheet}
              creatableLabel='Spreadsheet'
              creatableCallback={createNewSpreadsheet}
              component={HdFormikDropDown}
              onChangeEventHandler={onChangeSpreadsheetEventHandler}
            />
          </HdFormControl>
        </Grid>
      </Grid>
    </>
  );
}
